import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {DtoList} from 'features/core/shared/dto/dto.list';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsCrudService} from 'features/core/shared/service/crud/mvs-crud.service';

/**
 * navItem "Berechnung" of the Provisionszeile OBC, widget alias
 * `cp.provisionContract.calculation`.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * THE FD-7(a) / FD-8 PANE — the deferred calculation fields of the parent live here
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * These fields are produced by the calculation run and by nothing else. A Provisionszeile
 * created through the top-down wizard bypasses that pipeline, so on such a row the pane would
 * otherwise be a wall of blank cells — the framework even fills them with a bare '-'
 * (`widget.service.ts:509-511`), which is indistinguishable from a defect. Each of them therefore
 * renders "nicht berechnet" WHILE IT IS EMPTY, per field, applied by
 * {@link CpProvisionObjectDataComponent}; the explanation "Diese Werte ergänzt der nächste
 * Berechnungslauf." appears ONCE at the head of the block.
 *
 * WHY THE PER-FIELD TEXT PROMISES NOTHING (BT-12): an engine field is empty in three different
 * situations — the run has not come yet, the row is never selected by the run at all, or the run
 * RAN and found no applicable matrix and set the fields to null explicitly. "wird mit dem nächsten
 * Lauf berechnet" is only true in the first, so as a per-field statement it would be a fresh
 * claim of exactly the kind this whole duty exists to prevent.
 *
 * SCOPE OF THE DUTY (FD-8, signed) — the pending set is defined by NULLABILITY, and the entity is
 * the authority. Verified field by field on `ProvisionContract.java`:
 *
 *   provisionBase              :372  @ManyToOne(optional = true)                -> nullable, PENDING
 *   appliedBaseCalculationMode :395  @Column (no nullable=false, no @NotNull)   -> nullable, PENDING
 *   baseReferenceAmountMonthly :418  @Column(precision,scale) only              -> nullable, PENDING
 *   baseAmountMonthly          :441  @Column(precision,scale) only              -> nullable, PENDING
 *   baseAmountTotal            :463  @Column(precision,scale) only              -> nullable, PENDING
 *   agentsResolved             :487  @NotNull @Column(nullable=false) = FALSE   -> never null, see below
 *   eligibility                :330  @NotNull @Column(nullable=false)           -> NEVER EMPTY, NOT pending
 *
 * `eligibility` was removed from the pending set deliberately: it cannot be null, so a fallback on
 * it was dead code that suggested a safety net where none can ever fire. Under FD-8 the wizard
 * ASKS for the NOT NULL fields rather than guessing them.
 *
 * `baseReferenceAmountMonthly` was re-classified INTO the set on the same test. It has no
 * `@NotNull`, no `nullable = false` and no default, and the only writer is the run
 * (`ProvisionCalculationService.java:773,783,2305`) — which writes `0.00`, not null, for the
 * "genuinely new contract" case that has no previous value. So a null there does not mean "not
 * applicable"; it means the run has not touched this row yet, which is exactly the pending state.
 * An earlier revision of this class excluded it on the DIFFERENCE-mode argument; that argument was
 * wrong, because the not-applicable case is written as a zero.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * agentsResolved — the discriminated special case (BT-5)
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * The column is `Boolean.FALSE` by default and never null, so `false` carries TWO different
 * meanings and only one of them is pending:
 *
 *   * right after the wizard — Provisionssätze exist, but the flag has not been stamped yet.
 *     A bare "Nein" is factually wrong here: the rows are there.
 *   * after a run that resolved nothing — "der Lauf hat keinen Satz ermittelt". That is a
 *     legitimate and diagnostically valuable answer, and it is precisely what an operator is
 *     looking for when asking "warum keine Provisionssätze?". Masking it behind a pending hint
 *     would hide the finding permanently.
 *
 * The discriminator is therefore the EXISTENCE OF CHILD ROWS: `false` is treated as pending only
 * while at least one `cp.ProvisionContractAgent` hangs off this contract. With zero children the
 * real `false` is shown.
 *
 * WHY THIS COSTS ONE REQUEST (deviation from "reuse what the Provisionssätze pane loaded"): the
 * navItems are mutually exclusive `@if` branches in the view templates, so while "Berechnung" is
 * active {@link CpProvisionContractContractAgentsComponent} is NOT instantiated and has loaded
 * nothing to reuse. The probe is therefore made here — but kept to an existence question, not a
 * data load: `pageSize = 1`, no template, no sorting. The pane holds its skeleton until the probe
 * answers, so the widget is built ONCE with the final field lists instead of being rebuilt.
 */
@Component({
    selector: 'cp-provision-contract-calculation',
    templateUrl: './cp-provision-contract-calculation.component.html',
    styleUrls: ['./cp-provision-contract-calculation.component.scss'],
    standalone: false
})
export class CpProvisionContractCalculationComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionContractId: number;

    /** Fields per row — the side view passes 1. */
    @Input() columns: number = 2;

    /** Visible fields, in the order of the requirement. */
    readonly fields: string[] = [
        'eligibility',
        'provisionBaseDtoName',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal',
        'agentsResolved'
    ];

    /**
     * The five nullable calculation fields of the parent — see the nullability table in the class
     * javadoc. Each renders the pending hint while it is null.
     */
    readonly pendingFields: string[] = [
        'provisionBaseDtoName',
        'appliedBaseCalculationMode',
        'baseReferenceAmountMonthly',
        'baseAmountMonthly',
        'baseAmountTotal'
    ];

    /**
     * `agentsResolved`, but ONLY while child rows exist — see the discriminator in the class
     * javadoc. Empty as long as the contract has no Provisionssätze, so the real `false` survives.
     */
    pendingFalseFields: string[] = [];

    /** Does at least one `cp.ProvisionContractAgent` hang off this contract? */
    protected hasContractAgents: boolean;

    protected probeSubscription: Subscription;

    constructor(protected coreService: MvsCoreService) {
    }

    ngOnInit(): void {
        this.refreshComponent();
    }

    /** Runs the child-existence probe and only then releases the pane for rendering. */
    refreshComponent(): void {

        this.hasContractAgents = false;
        this.pendingFalseFields = [];

        if (!this.provisionContractId) {
            this.initialized = true;
            return;
        }

        const crudService: MvsCrudService = this.coreService.getCrudService('cp.ProvisionContractAgent');

        if (!crudService) {
            // No service, no discriminator. Fall back to showing the RAW value rather than
            // claiming a pending state we cannot substantiate — an unproven hint would be the
            // worse of the two errors.
            this.initialized = true;
            return;
        }

        this.busy = true;
        this.initialized = false;
        this.probeSubscription?.unsubscribe();

        // Existence probe, not a data load: no template, no sorting, one row at most.
        const request: ObjectRequestList = ObjectRequestList.createWithPaging(
            false,
            [FilterCriteria.create('provisionContract', FilterCriteria.cOperatorEqual, this.provisionContractId)],
            [],
            PagingDto.createSize(1)
        );

        this.probeSubscription = crudService.list(request).subscribe({
            next: (dtoList: DtoList) => {
                this.hasContractAgents = !!dtoList && !!dtoList.entries && dtoList.entries.length > 0;
                this.pendingFalseFields = this.hasContractAgents ? ['agentsResolved'] : [];
                this.busy = false;
                this.initialized = true;
            },
            error: () => {
                // Same reasoning as the missing-service branch: without the discriminator, show
                // the raw value.
                this.hasContractAgents = false;
                this.pendingFalseFields = [];
                this.busy = false;
                this.initialized = true;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionContractId']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.probeSubscription?.unsubscribe();
    }

}
